<script setup>
import { XMarkIcon } from '@heroicons/vue/24/outline'

const now = new Date()
const config = useRuntimeConfig()
const route = useRoute()

const { data } = await useFetch(config.public.BANNER_JSON_URL, {
  query: { affiliate: 'certificates_react' },
  getCachedData (key) {
    return useNuxtData(key).data.value
  },
  timeout: 5000,
  onResponseError () {
    return { data: [] }
  }
})

const phase = data.value?.flat()
  .map((item) => ({
    ...item,
    dateString: item.ends,
    remaining: new Date(item.ends) - now,
    prelude: stylize(item.prelude),
    subtitle: stylize(item.subtitle),
    homePrelude: stylize(item.homePrelude),
    header: stylize(item.header),
    homeHeader: stylize(item.homeHeader),
    title: stylize(item.title),
    addendumBefore: stylize(item.addendumBefore)
  }))
  .find(item => item.remaining > 0)

function stylize (string) {
  return string?.replace('[', '<span class="text-[#61DAFB]">').replace(']', '</span>')
}

const isBannerClosed = ref(true)

const closeBanner = () => {
  isBannerClosed.value = true
  localStorage.setItem('banner-react', false)
}

onMounted(() => {
  const banner = localStorage?.getItem('banner-react')

  if (banner === null) {
    isBannerClosed.value = false
  } else {
    isBannerClosed.value = true
  }
})

const showBannerOnLanding = computed(() => {
  if (route?.path.includes('/react') && !route?.path?.includes('/blog')) {
    return true
  } else {
    return phase.showOnHomepage || false
  }
})

const currentTheme = computed(() => {
  return phase?.theme || 1
})

const allowBannerOnPage = inject('allowBanners', true)
</script>

<template>
  <div
    v-show="!isBannerClosed && phase && showBannerOnLanding && allowBannerOnPage"
    class="relative">
    <NuxtLink
      :to="phase?.link ? phase?.link : '/react#'"
      :target="route?.path === '/' || phase?.external ? '_blank' : '_self'"
      class="dark group relative flex h-14 w-full justify-center bg-[#14424E]">
      <div
        v-if="currentTheme === 'react-day'"
        class="flex items-center justify-center gap-5 md:w-auto md:gap-10 xl:gap-10 2xl:gap-16">
        <div
          v-if="phase?.prelude"
          class="flex items-center gap-2 md:flex md:gap-2">
          <NuxtImg
            src="/images/react-logo.svg"
            class="h-auto w-6"
            alt="react-day-logo" />
          <div
            class=" hidden w-12 !text-center text-[10px] font-bold uppercase leading-none text-white md:block md:w-auto md:text-base [&>span]:text-[#61DAFB]"
            v-html="phase?.prelude" />
        </div>

        <div class="flex items-center gap-3 md:gap-4 xl:gap-8 2xl:gap-10">
          <div class="flex max-w-[180px] shrink-0 flex-col flex-wrap items-center justify-center gap-x-2 md:w-auto md:max-w-fit md:shrink md:flex-nowrap lg:flex-row lg:gap-8">
            <div
              v-if="phase?.prelude"
              class="block text-center text-[10px] font-normal uppercase text-white md:hidden md:text-base xl:text-base [&>span]:font-bold [&>span]:text-[#61DAFB]"
              v-html="phase?.prelude" />
            <div
              v-if="phase?.title"
              class="text-center text-[11px] font-semibold leading-none text-white md:whitespace-nowrap md:text-base md:!leading-[1.2rem] xl:text-base xl:leading-[1.2rem] [&>span]:text-[#61DAFB]"
              v-html="phase?.title" />
            <NuxtImg
              src="/images/react-day-speakers2.png"
              class="hidden h-8 w-auto lg:block"
              alt="react-day-speakers" />
            <div
              v-if="phase?.subtitle"
              class="block text-center text-[10px] font-normal text-white md:text-base xl:inline xl:text-base"
              v-html="phase?.subtitle" />
          </div>
          <div
            v-if="phase?.interlude"
            class="hidden text-center text-[10px] font-normal text-white md:block md:text-base xl:inline xl:text-base [&>span]:font-bold [&>span]:text-azure-radiance"
            v-html="phase?.interlude" />
          <div
            v-if="phase?.cta"
            class="hidden h-[30px] items-center justify-center gap-2 rounded-md bg-[#61DAFB] px-4 text-sm  font-bold text-black group-hover:bg-white group-hover:text-eerie-black md:inline-flex">
            <span>{{ phase?.cta }}</span>
            <span><svg
              width="12"
              height="12"
              viewBox="0 0 14 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6.6 1.5L10.2 5L3.50457e-07 5L5.25303e-07 7L10.2 7L6.6 10.5L8.1 11.9L14 6L8.1 0.100003L6.6 1.5Z"
                fill="currentColor" />
            </svg></span>
            <span />
          </div>
          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center xl:flex-row xl:gap-4">
            <span
              class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-xs md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-white p-1 md:px-2 md:py-1 bg-azure-radiance/20"
              box-style="" />
          </div>

          <div
            v-if="phase?.addendum"
            class="w-10 text-center text-[10px] font-bold text-[#61DAFB] md:w-auto md:px-4 md:py-1 md:text-base"
            style="letter-spacing: 1.2px">
            {{ phase?.addendum }}
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'react-offer'"
        class="flex items-center justify-center gap-2 md:w-auto md:gap-10 xl:gap-10 2xl:gap-16">
        <div
          v-if="phase?.prelude"
          class="hidden items-center gap-2 md:flex md:gap-2">
          <div
            class="w-12 !text-center text-[10px] font-bold leading-none text-white md:block md:w-auto md:text-lg [&>span]:text-[#61DAFB]"
            v-html="phase?.prelude" />
        </div>

        <div class="flex items-center gap-3 md:gap-4 xl:gap-8 2xl:gap-10">
          <div class="flex  shrink-0 flex-col flex-wrap items-center justify-center gap-x-2 md:w-auto md:max-w-fit md:shrink md:flex-nowrap lg:flex-row lg:gap-8">
            <div
              v-if="phase?.prelude"
              class="text-center text-xs font-semibold text-white md:hidden md:text-base xl:text-base [&>span]:font-bold [&>span]:text-[#61DAFB]"
              v-html="phase?.prelude" />
            <div class="flex flex-col items-center gap-2 md:flex-row">
              <NuxtImg
                src="/images/fire-react.svg"
                class="hidden h-auto w-6 lg:block"
                alt="ang-day-logo" />
              <div
                v-if="phase?.title"
                class="text-center text-xs font-semibold leading-none text-white md:whitespace-nowrap md:text-base md:!leading-[1.2rem] xl:text-base xl:leading-[1.2rem] [&>span]:text-[#61DAFB]"
                v-html="phase?.title" />
              <div
                v-if="phase?.subtitle"
                class="block text-center text-[9px] font-normal !leading-none text-white md:text-base xl:inline xl:text-base [&>span]:text-[#61DAFB]"
                v-html="phase?.subtitle" />
            </div>
          </div>
          <div
            v-if="phase?.interlude"
            class="hidden text-center text-[10px] font-normal text-white md:block md:text-base xl:inline xl:text-base [&>span]:font-bold [&>span]:text-azure-radiance"
            v-html="phase?.interlude" />
          <div
            v-if="phase?.cta"
            class="hidden h-[30px] items-center justify-center gap-2 rounded-md bg-[#61DAFB] px-4 text-sm  font-bold text-black group-hover:bg-white group-hover:text-eerie-black">
            <span>{{ phase?.cta }}</span>
            <span><svg
              width="12"
              height="12"
              viewBox="0 0 14 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6.6 1.5L10.2 5L3.50457e-07 5L5.25303e-07 7L10.2 7L6.6 10.5L8.1 11.9L14 6L8.1 0.100003L6.6 1.5Z"
                fill="currentColor" />
            </svg></span>
            <span />
          </div>
          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center xl:flex-row xl:gap-4">
            <span
              class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-xs md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-white !p-0 sm:!p-1 md:!px-2 md:!py-1 sm:bg-[#61DAFB]/20"
              box-style="" />
          </div>

          <div class="flex flex-col items-center gap-x-2 gap-y-0 md:flex-row">
            <p
              v-if="phase?.preAddendum"
              class="text-[10px] font-bold uppercase text-white">
              {{ phase?.preAddendum }}
            </p>
            <div
              v-if="phase?.addendum"
              class=" rounded-md text-center text-[10px] font-bold text-white md:w-auto md:bg-[#61DAFB]/20 md:p-2 md:px-4 md:py-1 md:text-base"
              style="letter-spacing: 1.2px">
              {{ phase?.addendum }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="currentTheme === 'react-launch-eb'"
        class="flex items-center justify-center gap-2 md:w-auto md:gap-10 xl:gap-10 2xl:gap-16">
        <div
          v-if="phase?.prelude"
          class="hidden items-center gap-2 md:flex md:gap-2">
          <NuxtImg
            src="/images/fire-react.svg"
            class="hidden h-auto w-6 lg:block"
            alt="ang-day-logo" />
          <div
            class="w-12 !text-center text-[10px] font-bold leading-none text-white md:block md:w-auto md:text-base [&>span]:text-[#61DAFB]"
            v-html="phase?.prelude" />
        </div>

        <div class="flex items-center gap-3 md:gap-4 xl:gap-8 2xl:gap-10">
          <div class="flex  shrink-0 flex-col flex-wrap items-center justify-center gap-x-2 md:w-auto md:max-w-fit md:shrink md:flex-nowrap lg:flex-row lg:gap-8">
            <div
              v-if="phase?.prelude"
              class="text-center text-xs font-semibold text-white md:hidden md:text-base xl:text-base [&>span]:font-bold [&>span]:text-[#61DAFB]"
              v-html="phase?.prelude" />
            <div class="flex flex-col items-center gap-2 md:flex-row">
              <div
                v-if="phase?.title"
                class="text-center text-xs font-semibold leading-none text-white md:whitespace-nowrap md:text-base md:!leading-[1.2rem] xl:text-base xl:leading-[1.2rem] [&>span]:text-[#61DAFB]"
                v-html="phase?.title" />
              <div
                v-if="phase?.subtitle"
                class="block text-center text-[9px] font-normal !leading-none text-white md:text-base xl:inline xl:text-base [&>span]:text-[#61DAFB]"
                v-html="phase?.subtitle" />
            </div>
          </div>
          <div
            v-if="phase?.interlude"
            class="w-20 text-center text-[8px] font-normal text-white md:block md:w-auto md:text-sm xl:inline xl:text-base [&>span]:font-bold [&>span]:text-azure-radiance"
            v-html="phase?.interlude" />
          <div
            v-if="phase?.cta"
            class="hidden h-[30px] items-center justify-center gap-2 rounded-md bg-[#61DAFB] px-4 text-sm font-bold  text-black group-hover:bg-white group-hover:text-eerie-black md:flex">
            <span>{{ phase?.cta }}</span>
            <span><svg
              width="12"
              height="12"
              viewBox="0 0 14 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg">
              <path
                d="M6.6 1.5L10.2 5L3.50457e-07 5L5.25303e-07 7L10.2 7L6.6 10.5L8.1 11.9L14 6L8.1 0.100003L6.6 1.5Z"
                fill="currentColor" />
            </svg></span>
            <span />
          </div>
          <div
            v-if="phase?.showCountdown"
            class="flex flex-col items-center xl:flex-row xl:gap-4">
            <span
              v-if="phase?.id?.includes('EXT')"
              class="whitespace-nowrap text-[8px] font-bold uppercase text-[#61DAFB] md:text-[12px] md:tracking-widest xl:mr-9">
              EXTENDED
            </span>
            <span
              class="whitespace-nowrap text-[8px] font-bold uppercase text-white md:text-xs md:tracking-widest">
              {{ phase?.countdownFooter }}
            </span>
            <AppCountdown
              v-bind="{
                remaining: phase?.remaining,
                daysToHours: phase?.showDaysAsHours,
              }"
              class="dark"
              box-class="text-white !p-0 sm:!p-1 md:!px-2 md:!py-1 sm:bg-[#61DAFB]/20"
              box-style="" />
          </div>

          <div class="flex flex-col items-center gap-x-2 gap-y-0 md:flex-row">
            <p
              v-if="phase?.preAddendum"
              class="text-[10px] font-bold uppercase text-white">
              {{ phase?.preAddendum }}
            </p>
            <div
              v-if="phase?.addendum"
              class=" rounded-md text-center text-[10px] font-bold text-white md:w-auto md:bg-[#61DAFB]/20 md:p-2 md:px-4 md:py-1 md:text-base"
              style="letter-spacing: 1.2px">
              {{ phase?.addendum }}
            </div>
          </div>
        </div>
      </div>
    </NuxtLink>
    <button
      class="absolute right-2 top-4 z-20 p-1 hover:bg-white/20 xl:right-3"
      @click="closeBanner">
      <span class="sr-only">close</span><XMarkIcon class="x-5 h-5 text-white" />
    </button>
  </div>
</template>

<style scoped>
.banner-text :deep(span) {
  @apply  text-nuxt-green
}
</style>
